import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { isValidAadhaar, isValidMobileNumber, validatePAN } from '../../../utils/utils';
import { getUserDetailsByID, postImageUrl, updateUserProfile } from '../../../actions/users';
import { SkeltonCardTwo } from '../../../common/SkeltonCardTwo';
import { closeModalAction } from '../../../actions/modal';
import Upload from '../../../common/Form/FileUpload';
import { PAN_OR_AADHAR_LOADING } from '../../../types/loading';
import axiosInstance from '../../../apis/axios';
import config from '../../../config';
import { getNewAuthToken, getOrgId } from '../../../utils/common';
import FullScreenLoader from '../../../components/FullScreenLoader';
import { CLEAR_UPDATE_DETAILS_KYC } from '../../../types/user';
const formName = 'updateUser';

const UpdateUser = ({ user }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const isPancardOrAadharUploading = useSelector((state) => state.loading.isPancardOrAadharUploading);
    const inputBrowseFileRef = useRef(null);

    const defaultValues = {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        org_name: user.org_name,
        mobile: user.mobile,
    };

    const userDetailsById = useSelector((state) => state.user.userDetailsById);
    const isUserDetailsByIdLoading = useSelector((state) => state.loading.isUserDetailsByIdLoading);
    const methods = useForm({ defaultValues });


    const memoizedUserDetails = useMemo(() => {
        let data = { user: {}, org: {} };
        if (userDetailsById[user.id]) {
            data = userDetailsById[user.id];
            if (data) {
                methods.setValue('first_name', data?.user?.first_name);
                methods.setValue('last_name', data?.user?.last_name);
                methods.setValue('email', data?.user?.email);
                methods.setValue('org_name', data?.org?.name);
                if (data?.user?.mobile && data?.user?.mobile.startsWith('+91')) methods.setValue('mobile', data?.user?.mobile.substring(3));
                else methods.setValue('mobile', data?.user?.mobile);
            }
        }
        return data;
    }, [userDetailsById, user.id, methods]);

    useEffect(() => {
        dispatch(getUserDetailsByID({ user_id: user.id }));
    }, [user.id, dispatch]);


    useEffect(() => {
        return () => {
            const toRemove = ['kyc-pancard-front', 'kyc-aadhar-front', 'kyc-aadhar-back', `${user.id}-avatar`];
            dispatch({ type: CLEAR_UPDATE_DETAILS_KYC, payload: toRemove })
        };
    }, [dispatch, user.id])

    const inviteFormImagesUrls = useSelector((state) => state.user.inviteFormImagesUrls);
    const avatarUrl = inviteFormImagesUrls[`${user.id}-avatar`];

    const submit = (formData) => {
        const pancardImage = inviteFormImagesUrls['kyc-pancard-front'];
        const aadharImage = inviteFormImagesUrls['kyc-aadhar-front'];
        const aadharBackImage = inviteFormImagesUrls['kyc-aadhar-back'];
        const payload = {
            full_name: formData.first_name + ' ' + formData.last_name,
            email: formData.email,
            org_name: formData.org_name,
            avatar_link: avatarUrl,
            mobile: formData.mobile,
        };

        let kyc = [];
        if (formData['kyc-pancard']) {
            kyc.push({
                ref: 'kyc-pancard',
                value: formData['kyc-pancard'],
            })
        }
        if (formData['kyc-aadhar']) {
            kyc.push({
                ref: 'kyc-aadhar',
                value: formData['kyc-aadhar'],
            })
        };
        if (aadharImage) {
            kyc.push({
                ref: 'kyc-aadhar-front',
                value: aadharImage,
            })
        };
        if (aadharBackImage) {
            kyc.push({
                ref: 'kyc-aadhar-back',
                value: aadharBackImage,
            })
        };

        if (pancardImage) {
            kyc.push({
                ref: 'kyc-pancard-front',
                value: pancardImage,
            })
        };

        if (kyc && kyc.length > 0) payload.kyc_docs = kyc;

        dispatch(updateUserProfile({ payload, userId: user.id, formName, refreshList: true }));
    };

    const uploadImage = (e) => {
        dispatch(postImageUrl({ files: Array.from(e.target.files), ref: `${user.id}-avatar`, type: 'avatar', purpose: 'AVATAR', user_id: user.id }));
    };

    if (isUserDetailsByIdLoading) return <div className='flex justify-center items-center h-full px-6'>
        <SkeltonCardTwo arr={[1, 2, 3, 4]} height={20} />
    </div>;

    const isAdhaarExists = memoizedUserDetails?.user?.kyc?.find((e) => e.ref === 'kyc-aadhar');
    const isPancardExists = memoizedUserDetails?.user?.kyc?.find((e) => e.ref === 'kyc-pancard');


    const onSuccess = async (file_url, ref) => {
        try {
            dispatch({ type: PAN_OR_AADHAR_LOADING, payload: true });
            if (ref === 'kyc-aadhar-front' || ref === 'kyc-pancard-front') {
                const response = await axiosInstance.post(
                    `${config.api.streamV2ApiUrl}/utils/extractor-kyc`,
                    {
                        file_url,
                        event_type: "KYC",
                        user_id: user.id
                    },
                    {
                        headers: {
                            'x-org-id': getOrgId(),
                            'authorization': `Bearer ${getNewAuthToken()}`
                        }
                    }
                );
                const data = response?.data?.data;
                if (ref === 'kyc-aadhar-front' && data) {
                    if (data.is_valid_adhaar && data.adhaar_number) {

                        const formattedAadhar = data.adhaar_number.replace(/\s+/g, '');
                        methods.setValue('kyc-aadhar', formattedAadhar, { shouldValidate: true, shouldDirty: true });
                    } else {
                        dispatch({
                            type: 'toast',
                            payload: {
                                message: 'Unable to fetch Aadhar details. Please try again later.',
                                type: 'error'
                            }
                        })
                    }
                }

                if (ref === 'kyc-pancard-front' && data) {
                    if (data.is_valid_pan && data.pan_number) {
                        methods.setValue('kyc-pancard', data.pan_number, { shouldValidate: true, shouldDirty: true });
                    } else {
                        dispatch({
                            type: 'toast',
                            payload: {
                                message: 'Unable to fetch PAN details. Please try again later.',
                                type: 'error'
                            }
                        })
                    }
                }
            }
        } catch (error) {
            console.log('Error in KYC extraction:', error);
        }
        dispatch({ type: PAN_OR_AADHAR_LOADING, payload: false });
    };

    const onFileUpload = (files, ref) => {
        let purpose = '';
        if (ref === 'avatar_link') purpose = 'AVATAR';
        else if (ref === 'kyc-aadhar-front') purpose = 'USER_AADHAAR';
        else if (ref === 'kyc-aadhar-back') purpose = 'USER_AADHAAR';
        else if (ref === 'kyc-pancard-front') purpose = 'USER_PANCARD';
        else if (ref === 'kyc-pancard-back') purpose = 'USER_PANCARD';
        dispatch(postImageUrl({ files, ref, purpose, user_id: user.id, additionalData: { user_id: user.id }, onSuccess: (e) => onSuccess(e, ref), panOrAadharUpload: true }));
    }

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            formName={formName}
            submitButtonText={'Update'}
            onCancel={() => dispatch(closeModalAction())}
            showSubmit
            submitContainerClassName='flex gap-2 justify-end py-4 px-4'
            isLoading={formButtonLoading[formName]}
        >
            {isPancardOrAadharUploading && <FullScreenLoader className='absolute inset-0' />}
            <div className='flex  items-center py-2'>
                <div className='relative w-24 h-24 m-4'>
                    <img
                        src={avatarUrl ? avatarUrl : user.avatar_link}
                        alt=''
                        loading='lazy'
                        className='w-24 h-24 rounded-full object-cover hover:opacity-25  cursor-pointer border-2'

                    />
                    <div className='absolute  opacity-0 hover:opacity-100 top-0 group w-full h-full bg-black rounded-full  bg-opacity-25 flex items-center justify-center'>
                        <span
                            onClick={() => inputBrowseFileRef?.current?.click()}
                            className='material-icons hover:bg-white   rounded-full cursor-pointer hover:bg-opacity-50 p-2 text-font24 focus:outline-none text-white  transition duration-200'
                        >
                            photo_camera
                        </span>
                        <input accept='image/*' className='hidden' onChange={uploadImage} type='file' id='file' ref={inputBrowseFileRef} />
                    </div>
                </div>
                <div className='grid grid-cols-2 rows-fr'>
                    <Input label='First Name' name='first_name' required type={'text'} className='w-[30.5rem] ' />
                    <Input label='Last Name' name='last_name' required type={'text'} className='w-[30.5rem] ' />
                    <Input
                        label='Email Address'
                        name='email'
                        required
                        validate={(email) => {
                            return (
                                email.match(
                                    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                                ) || 'Invalid Email'
                            );
                        }}
                    />
                    <Input
                        className='w-full'
                        label='Mobile'
                        type={'number'}
                        name='mobile'
                        validate={(mobile) => {
                            if (mobile === '') return true;
                            return mobile.length === 10 || 'Invalid Mobile';
                        }}
                        required
                    />
                    {/* <Input validate={(mobile) => {
                        if (mobile === '') return true;
                        return isValidMobileNumber(mobile) || 'Invalid Mobile';
                    }} label='Mobile Number' name='mobile' required /> */}
                    {memoizedUserDetails?.org && <Input label='Company Name' name='org_name' required />}


                </div>
            </div>
            <div className='px-3 grid grid-cols-3 rows-fr grid-flow-row-dense'>
                {!isAdhaarExists && <>
                    <Upload onChange={e => onFileUpload(e, 'kyc-aadhar-front')} required name='aadhar_front_image' label='Aadhar Front Image' />
                    <Upload onChange={e => onFileUpload(e, 'kyc-aadhar-back')} name='aadhar_back_image' label='Aadhar Back Image' />
                    <Input
                        readOnly={true}
                        label="Aadhaar Number"
                        name="kyc-aadhar"
                        shouldUnregister
                        validate={(aadhar) => {
                            if (aadhar === '') return true;
                            return isValidAadhaar(aadhar) || 'Invalid Aadhaar';
                        }}
                    />
                </>}
                {!isPancardExists &&
                    <>
                        <Upload onChange={e => onFileUpload(e, 'kyc-pancard-front')} name='pancard_front_image' label='Pan Front Image' />
                        <Input
                            readOnly={true}
                            label="Pancard Number"
                            name="kyc-pancard"
                            shouldUnregister
                            validate={(pan) => {
                                if (pan === '') return true;
                                return (validatePAN(pan) || 'Invalid Pancard');
                            }}
                        />
                    </>
                }
            </div>
        </Form>
    );
};


export default UpdateUser;
